import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  Eye,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  CheckCircle,
  Package,
  ShoppingBag,
  TrendingUp,
  Plus,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Star,
  StarOff,
  Tag,
  Calendar,
  Eye as EyeIcon,
  Image,
  XCircle,
  Zap,
  Info,
  Save,
  Upload,
  PlusCircle,
  AlertTriangle,
} from "lucide-react";
import { productService } from "../../api/services/productService";
import { format, formatDistance } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Helper Functions
const getCategoryInfo = (category) => {
  const categories = {
    food: {
      icon: ShoppingBag,
      label: "Food",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    "non-food": {
      icon: Package,
      label: "Non-Food",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
  };
  return categories[category] || categories.food;
};

const getStatusInfo = (isActive, isAvailable) => {
  if (isActive && isAvailable) {
    return {
      icon: CheckCircle,
      label: "Active",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    };
  } else if (!isActive) {
    return {
      icon: XCircle,
      label: "Inactive",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    };
  } else {
    return {
      icon: AlertTriangle,
      label: "Out of Stock",
      color: "text-orange-500",
      bg: "bg-orange-50 dark:bg-orange-900/20",
      border: "border-orange-200 dark:border-orange-800",
    };
  }
};

const formatTime = (date) => {
  return formatDistance(new Date(date), new Date(), { addSuffix: true });
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
    <div className="relative p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md">
          <Icon size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          <TrendingUp size={12} className="text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">
            +{trend}%
          </span>
          <span className="text-gray-500">vs last week</span>
        </div>
      )}
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500" />
  </motion.div>
);

// Product Card Component
const ProductCard = ({
  product,
  isSelected,
  onSelect,
  onViewDetails,
  onEdit,
  onToggleStatus,
  onToggleFeatured,
  onToggleAvailability,
  onDelete,
}) => {
  const category = getCategoryInfo(product.category);
  const status = getStatusInfo(product.isActive, product.isAvailable);
  const CategoryIcon = category.icon;
  const StatusIcon = status.icon;

  const isDeactivated = !product.isActive;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${
        isDeactivated
          ? "border-red-200/50 dark:border-red-800/50 opacity-75"
          : "border-gray-200/50 dark:border-gray-800/50"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(product.id)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </div>

          {product.imageBase64 && (
            <div className="flex-shrink-0">
              <img
                src={product.imageBase64}
                alt={product.name}
                className={`w-16 h-16 object-cover rounded-lg ${
                  isDeactivated ? "grayscale" : ""
                }`}
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
                  >
                    <StatusIcon size={10} /> {status.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${category.bg} ${category.color} border ${category.border}`}
                  >
                    <CategoryIcon size={10} /> {category.label}
                  </span>
                  {product.isFeatured && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
                      <Star size={10} /> Featured
                    </span>
                  )}
                  {product.isNew && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                      <Zap size={10} /> New
                    </span>
                  )}
                  {isDeactivated && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800">
                      <XCircle size={10} /> Deactivated
                    </span>
                  )}
                </div>
                <h3
                  className={`text-base font-semibold mb-1 ${
                    isDeactivated
                      ? "text-gray-500 dark:text-gray-500 line-through"
                      : "text-gray-900 dark:text-white"
                  }`}
                >
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                  {product.shortDescription ||
                    product.description?.substring(0, 100)}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Tag size={12} /> SKU: {product.sku}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} /> {formatTime(product.createdAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    <EyeIcon size={12} /> {product.viewCount} views
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onViewDetails(product)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="View Details"
                >
                  <Eye size={14} className="text-blue-500" />
                </button>
                <button
                  onClick={() => onEdit(product)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Edit"
                >
                  <Edit2 size={14} className="text-green-500" />
                </button>
                <button
                  onClick={() =>
                    onToggleFeatured(product.id, !product.isFeatured)
                  }
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={
                    product.isFeatured ? "Remove Featured" : "Mark as Featured"
                  }
                >
                  {product.isFeatured ? (
                    <Star size={14} className="text-yellow-500" />
                  ) : (
                    <StarOff size={14} className="text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => onToggleStatus(product.id, !product.isActive)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={product.isActive ? "Deactivate" : "Activate"}
                >
                  {product.isActive ? (
                    <ToggleRight size={14} className="text-green-500" />
                  ) : (
                    <ToggleLeft size={14} className="text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() =>
                    onToggleAvailability(product.id, !product.isAvailable)
                  }
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={
                    product.isAvailable
                      ? "Mark as Unavailable"
                      : "Mark as Available"
                  }
                >
                  {product.isAvailable ? (
                    <CheckCircle size={14} className="text-emerald-500" />
                  ) : (
                    <AlertTriangle size={14} className="text-orange-500" />
                  )}
                </button>
                <button
                  onClick={() => onDelete(product)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Create/Edit Product Modal Component
const CreateEditProductModal = ({ product, onClose, onSuccess }) => {
  const isEditing = !!product?.id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  const { success: toastSuccess, error: toastError } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    category: "food",
    description: "",
    shortDescription: "",
    isAvailable: true,
    isFeatured: false,
    isNew: false,
    isActive: true,
    specifications: {},
    features: [],
    imageAlt: "",
  });

  // Specifications state
  const [specsList, setSpecsList] = useState([]);

  // Features state
  const [featuresList, setFeaturesList] = useState([]);
  const [newFeature, setNewFeature] = useState("");

  // Image states
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const mainImageInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Helper function to generate SKU based on category
  const generateSKU = (category) => {
    const prefix = category === "food" ? "FOOD" : "NON-FOOD";
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${prefix}-${timestamp}-${random}`;
  };

  // Helper function to get full image URL
  const getFullImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    if (path.startsWith("data:")) {
      return path;
    }
    if (path.startsWith("/uploads")) {
      const apiBaseUrl =
        import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
      return `${apiBaseUrl}${path}`;
    }
    return path;
  };

  // Initialize form with product data if editing
  useEffect(() => {
    if (product && isEditing) {
      setFormData({
        name: product.name || "",
        category: product.category || "food",
        description: product.description || "",
        shortDescription: product.shortDescription || "",
        isAvailable:
          product.isAvailable !== undefined ? product.isAvailable : true,
        isFeatured: product.isFeatured || false,
        isNew: product.isNew || false,
        isActive: product.isActive !== undefined ? product.isActive : true,
        specifications: product.specifications || {},
        features: product.features || [],
        imageAlt: product.imageAlt || "",
      });

      // Handle specifications
      if (
        product.specifications &&
        typeof product.specifications === "object"
      ) {
        const specs = Object.entries(product.specifications).map(
          ([key, value]) => ({
            key,
            value: String(value),
          }),
        );
        setSpecsList(specs);
      } else {
        setSpecsList([]);
      }

      // Handle features
      if (product.features && Array.isArray(product.features)) {
        setFeaturesList(product.features);
      } else {
        setFeaturesList([]);
      }

      // Handle main image preview - SUPPORT BOTH OLD AND NEW FORMATS
      if (product.mainImagePath) {
        // New path-based format
        setMainImagePreview(getFullImageUrl(product.mainImagePath));
      } else if (product.imageBase64) {
        // Legacy BLOB format
        setMainImagePreview(product.imageBase64);
      } else if (product.imageBlob) {
        // Another legacy format
        setMainImagePreview(
          `data:${product.imageMimeType};base64,${product.imageBlob}`,
        );
      } else {
        setMainImagePreview(null);
      }

      // Handle gallery images preview - SUPPORT BOTH OLD AND NEW FORMATS
      if (product.galleryImages && product.galleryImages.length > 0) {
        const previews = [];
        product.galleryImages.forEach((img) => {
          if (img.path) {
            // New path-based format
            previews.push(getFullImageUrl(img.path));
          } else if (img.data) {
            // Legacy BLOB format
            previews.push(`data:${img.mimeType};base64,${img.data}`);
          }
        });
        setGalleryPreviews(previews);
      } else {
        setGalleryPreviews([]);
      }
    }
  }, [product, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addSpecification = () => {
    setSpecsList([...specsList, { key: "", value: "" }]);
  };

  const updateSpecification = (index, field, value) => {
    const updated = [...specsList];
    updated[index][field] = value;
    setSpecsList(updated);

    const specs = {};
    updated.forEach((spec) => {
      if (spec.key && spec.key.trim()) {
        specs[spec.key.trim()] = spec.value;
      }
    });
    setFormData((prev) => ({ ...prev, specifications: specs }));
  };

  const removeSpecification = (index) => {
    const updated = specsList.filter((_, i) => i !== index);
    setSpecsList(updated);

    const specs = {};
    updated.forEach((spec) => {
      if (spec.key && spec.key.trim()) {
        specs[spec.key.trim()] = spec.value;
      }
    });
    setFormData((prev) => ({ ...prev, specifications: specs }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      const updated = [...featuresList, newFeature.trim()];
      setFeaturesList(updated);
      setFormData((prev) => ({ ...prev, features: updated }));
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    const updated = featuresList.filter((_, i) => i !== index);
    setFeaturesList(updated);
    setFormData((prev) => ({ ...prev, features: updated }));
  };

  const handleMainImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toastError("Only image files are allowed (JPEG, PNG, GIF, WEBP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toastError("Image size must be less than 5MB");
      return;
    }

    setMainImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= 5 * 1024 * 1024;

      if (!isValidType)
        toastError(`Invalid type: ${file.name}. Only images allowed.`);
      if (!isValidSize) toastError(`${file.name} exceeds 5MB limit.`);

      return isValidType && isValidSize;
    });

    setGalleryImages([...galleryImages, ...validFiles]);

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const removeMainImage = () => {
    setMainImage(null);
    setMainImagePreview(null);
    if (mainImageInputRef.current) {
      mainImageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Product name is required");
      toastError("Product name is required");
      return;
    }
    if (!formData.category) {
      setError("Category is required");
      toastError("Category is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const productData = {
        // Generate SKU only for new products
        ...(isEditing ? {} : { sku: generateSKU(formData.category) }),
        name: formData.name.trim(),
        category: formData.category,
        description: formData.description || undefined,
        shortDescription: formData.shortDescription || undefined,
        isAvailable: formData.isAvailable,
        isFeatured: formData.isFeatured,
        isNew: formData.isNew,
        isActive: formData.isActive,
        imageAlt: formData.imageAlt || undefined,
      };

      if (specsList.length > 0) {
        const specs = {};
        specsList.forEach((spec) => {
          if (spec.key && spec.key.trim()) {
            specs[spec.key.trim()] = spec.value;
          }
        });
        if (Object.keys(specs).length > 0) {
          productData.specifications = specs;
        }
      }

      if (featuresList.length > 0) {
        productData.features = featuresList;
      }

      // Only send images if they are new File objects
      const mainImageToSend =
        mainImage instanceof File && mainImage.size > 0 ? mainImage : null;

      const galleryImagesToSend =
        galleryImages && galleryImages.length > 0
          ? galleryImages.filter((img) => img instanceof File && img.size > 0)
          : null;

      // DELETE FLAGS
      if (isEditing) {
        // If new main image is being uploaded, delete old one
        if (mainImageToSend) {
          productData.deleteOldMainImage = true;
        }

        // If new gallery images are being uploaded, delete old ones
        if (galleryImagesToSend && galleryImagesToSend.length > 0) {
          productData.deleteOldGalleryImages = true;
        }
      }

      if (isEditing) {
        await productService.updateProduct(
          product.id,
          productData,
          mainImageToSend,
          galleryImagesToSend,
        );
        toastSuccess("Product updated successfully!");
      } else {
        await productService.createProduct(
          productData,
          mainImageToSend,
          galleryImagesToSend,
        );
        toastSuccess("Product created successfully!");
      }

      onSuccess();
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors
          .map((e) => `${e.path}: ${e.msg}`)
          .join(", ");
        setError(errorMessages);
        toastError(errorMessages);
      } else {
        setError(
          err.message || `Failed to ${isEditing ? "update" : "create"} product`,
        );
        toastError(
          err.message || `Failed to ${isEditing ? "update" : "create"} product`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info", icon: Package },
    { id: "details", label: "Details", icon: Info },
    { id: "specs", label: "Specifications", icon: Tag },
    { id: "features", label: "Features", icon: Star },
    { id: "images", label: "Images", icon: Image },
  ];

  const CategoryOption = ({ value, label, icon: Icon }) => (
    <label
      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
        formData.category === value
          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
      }`}
    >
      <input
        type="radio"
        name="category"
        value={value}
        checked={formData.category === value}
        onChange={handleChange}
        className="hidden"
      />
      <Icon
        size={24}
        className={
          formData.category === value ? "text-primary-600" : "text-gray-400"
        }
      />
      <div>
        <p className="font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500">
          {value === "food"
            ? "Edible products, groceries, beverages"
            : "Non-food items, equipment, supplies"}
        </p>
      </div>
    </label>
  );

  const renderBasicInfo = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Premium Potato Chips"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Category <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <CategoryOption value="food" label="Food" icon={ShoppingBag} />
          <CategoryOption value="non-food" label="Non-Food" icon={Package} />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Short Description
        </label>
        <textarea
          name="shortDescription"
          value={formData.shortDescription}
          onChange={handleChange}
          rows={2}
          placeholder="Brief product description (max 500 characters)"
          maxLength={500}
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.shortDescription.length}/500 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Full Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={6}
          placeholder="Detailed product description, features, benefits..."
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
        />
      </div>

      <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Status Settings
        </h3>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Active Product
              </span>
              <p className="text-xs text-gray-500">
                Product visible and available for purchase
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                formData.isActive
                  ? "bg-primary-600"
                  : "bg-gray-200 dark:bg-gray-700"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  formData.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                In Stock / Available
              </span>
              <p className="text-xs text-gray-500">
                Product currently has inventory
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isAvailable: !prev.isAvailable,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                formData.isAvailable
                  ? "bg-primary-600"
                  : "bg-gray-200 dark:bg-gray-700"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  formData.isAvailable ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Featured Product
              </span>
              <p className="text-xs text-gray-500">
                Display on homepage and featured sections
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isFeatured: !prev.isFeatured,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                formData.isFeatured
                  ? "bg-primary-600"
                  : "bg-gray-200 dark:bg-gray-700"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  formData.isFeatured ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                New Product
              </span>
              <p className="text-xs text-gray-500">
                Mark as new arrival (30 days)
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, isNew: !prev.isNew }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                formData.isNew
                  ? "bg-primary-600"
                  : "bg-gray-200 dark:bg-gray-700"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  formData.isNew ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </label>
        </div>
      </div>
    </div>
  );

  const renderDetails = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Image Alt Text
        </label>
        <input
          type="text"
          name="imageAlt"
          value={formData.imageAlt}
          onChange={handleChange}
          placeholder="Descriptive text for product images (accessibility)"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
        />
        <p className="text-xs text-gray-500 mt-1">
          Helps with SEO and accessibility
        </p>
      </div>
    </div>
  );

  const renderSpecifications = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Product Specifications
          </h3>
          <p className="text-xs text-gray-500">
            Add technical details and attributes
          </p>
        </div>
        <button
          type="button"
          onClick={addSpecification}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <PlusCircle size={16} /> Add Specification
        </button>
      </div>

      <div className="space-y-3">
        {specsList.map((spec, index) => (
          <div key={index} className="flex gap-3 items-start">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Key (e.g., Weight, Brand, Origin)"
                value={spec.key}
                onChange={(e) =>
                  updateSpecification(index, "key", e.target.value)
                }
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Value (e.g., 200g, XYZ Corp, Italy)"
                value={spec.value}
                onChange={(e) =>
                  updateSpecification(index, "value", e.target.value)
                }
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => removeSpecification(index)}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}

        {specsList.length === 0 && (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <Tag size={32} className="mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-500">No specifications added yet</p>
            <p className="text-xs text-gray-400">
              Click "Add Specification" to start
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderFeatures = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Add Feature
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addFeature()}
            placeholder="e.g., Gluten-free, Organic, Eco-friendly"
            className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
          <button
            type="button"
            onClick={addFeature}
            className="px-4 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          >
            <Plus size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">Press Enter to add quickly</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {featuresList.map((feature, index) => (
          <div
            key={index}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full"
          >
            <CheckCircle size={12} className="text-green-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {feature}
            </span>
            <button
              type="button"
              onClick={() => removeFeature(index)}
              className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X size={12} className="text-gray-500" />
            </button>
          </div>
        ))}
      </div>

      {featuresList.length === 0 && (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <Star size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No features added yet</p>
          <p className="text-xs text-gray-400">
            Add key selling points and benefits
          </p>
        </div>
      )}
    </div>
  );

  const renderImages = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Main Product Image
        </h3>
        <div className="flex items-start gap-4">
          <div
            className={`relative w-32 h-32 rounded-xl border-2 border-dashed transition-all ${
              mainImagePreview
                ? "border-green-500 bg-green-50 dark:bg-green-900/10"
                : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800"
            } flex items-center justify-center overflow-hidden`}
          >
            {mainImagePreview ? (
              <>
                <img
                  src={mainImagePreview}
                  alt="Main product"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={removeMainImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <Image size={32} className="text-gray-400" />
            )}
          </div>
          <div>
            <input
              ref={mainImageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleMainImageUpload}
              className="hidden"
              id="main-image-upload"
            />
            <label
              htmlFor="main-image-upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <Upload size={16} /> Upload Image
            </label>
            <p className="text-xs text-gray-500 mt-2">
              Recommended: 800x800px, JPEG/PNG/WEBP, max 5MB
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Gallery Images
        </h3>
        <div className="flex flex-wrap gap-3 mb-4">
          {galleryPreviews.map((preview, index) => (
            <div
              key={index}
              className="relative w-24 h-24 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <img
                src={preview}
                alt={`Gallery ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeGalleryImage(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
              >
                <X size={10} />
              </button>
            </div>
          ))}
          <label
            htmlFor="gallery-upload"
            className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 transition-colors"
          >
            <Plus size={24} className="text-gray-400" />
            <span className="text-xs text-gray-500 mt-1">Add</span>
          </label>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            onChange={handleGalleryUpload}
            className="hidden"
            id="gallery-upload"
          />
        </div>
        <p className="text-xs text-gray-500">
          Additional product images (max 10). First image will be used as
          secondary thumbnail.
        </p>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicInfo();
      case "details":
        return renderDetails();
      case "specs":
        return renderSpecifications();
      case "features":
        return renderFeatures();
      case "images":
        return renderImages();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 sm:px-5 py-4 sm:p-5 flex items-center justify-between z-10">
          <div className="min-w-0 pr-3">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
              {isEditing ? "Edit Product" : "Create New Product"}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
              {isEditing
                ? "Update product information"
                : "Add a new product to your catalog"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs – fully responsive */}
        <div className="border-b border-gray-100 dark:border-gray-800">
          <div
            className="flex gap-1 px-2 sm:px-5 pt-2 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                  flex items-center justify-center gap-1.5 sm:gap-2
                  flex-shrink-0 snap-start
                  px-3 sm:px-4 py-2.5
                  text-xs sm:text-sm font-medium
                  rounded-t-xl transition-all whitespace-nowrap
                  min-w-[72px] sm:min-w-0
                  ${
                    isActive
                      ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 border-b-2 border-primary-500"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-b-2 border-transparent"
                  }
                `}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {/* Hide long labels on very small screens if needed – optional */}
                  <span className="hidden xs:inline sm:inline">
                    {tab.label}
                  </span>
                  {/* Always show short label on mobile */}
                  <span className="xs:hidden sm:hidden">
                    {tab.id === "basic"
                      ? "Basic"
                      : tab.id === "details"
                        ? "Details"
                        : tab.id === "specs"
                          ? "Specs"
                          : tab.id === "features"
                            ? "Features"
                            : "Images"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:p-6">
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-start sm:items-center gap-3 text-red-600 text-sm">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5 sm:mt-0" />
              <span className="break-words">{error}</span>
            </div>
          )}
          {renderActiveTab()}
        </div>

        {/* Footer – stacks on mobile */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 px-4 sm:px-5 py-3 sm:p-5 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 transition-all hover:opacity-90 text-sm sm:text-base"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {loading
              ? "Saving..."
              : isEditing
                ? "Update Product"
                : "Create Product"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Details Modal Component
const DetailsModal = ({ product, onClose, onEdit }) => {
  const category = getCategoryInfo(product.category);
  const status = getStatusInfo(product.isActive, product.isAvailable);
  const CategoryIcon = category.icon;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Product Details
            </h2>
            <p className="text-sm font-mono text-primary-600 dark:text-primary-400 mt-0.5">
              {product.sku}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {product.imageBase64 && (
            <div className="flex justify-center">
              <img
                src={product.imageBase64}
                alt={product.name}
                className="w-48 h-48 object-cover rounded-xl shadow-md"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
            >
              <StatusIcon size={14} /> {status.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${category.bg} ${category.color} border ${category.border}`}
            >
              <CategoryIcon size={14} /> {category.label}
            </span>
            {product.isFeatured && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
                <Star size={14} /> Featured
              </span>
            )}
            {product.isNew && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                <Zap size={14} /> New
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
            <div>
              <p className="text-xs text-gray-500">Name</p>
              <p className="text-sm font-medium">{product.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">SKU</p>
              <p className="text-sm">{product.sku}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Category</p>
              <p className="text-sm">{category.label}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">View Count</p>
              <p className="text-sm">{product.viewCount}</p>
            </div>
          </div>

          {product.shortDescription && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Short Description</h3>
              <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300">
                {product.shortDescription}
              </div>
            </div>
          )}

          {product.description && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Description</h3>
              <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                {product.description}
              </div>
            </div>
          )}

          {product.specifications &&
            Object.keys(product.specifications).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Specifications</h3>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 space-y-2">
                  {Object.entries(product.specifications).map(
                    ([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          {key}:
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {String(value)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {product.features && product.features.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Features</h3>
              <div className="flex flex-wrap gap-2">
                {product.features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <CheckCircle size={14} className="text-green-500" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
            <div>
              <span className="block">Created</span>
              <span className="text-gray-900 dark:text-white">
                {format(new Date(product.createdAt), "MMM d, yyyy HH:mm")}
              </span>
            </div>
            <div>
              <span className="block">Updated</span>
              <span className="text-gray-900 dark:text-white">
                {format(new Date(product.updatedAt), "MMM d, yyyy HH:mm")}
              </span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={() => {
              onClose();
              onEdit(product);
            }}
            className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          >
            Edit Product
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ product, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const { error: toastError } = useToast();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      toastError(err.message || "Failed to delete product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Trash2 size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Delete Product
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Are you sure you want to delete{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {product.name}
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Trash2 size={18} />
              )}
              {loading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, statusFilter, featuredFilter, searchTerm]);

  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, [
    categoryFilter,
    statusFilter,
    featuredFilter,
    sortBy,
    sortOrder,
    currentPage,
    searchTerm,
  ]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(categoryFilter !== "all" && { category: categoryFilter }),
        ...(statusFilter === "active" && { isActive: true }),
        ...(statusFilter === "inactive" && { isActive: false }),
        ...(featuredFilter !== "all" && {
          isFeatured: featuredFilter === "featured",
        }),
        ...(searchTerm && { search: searchTerm }),
      };

      console.log("📤 Fetching products with params:", params);
      const response = await productService.getAllProducts(params);

      let productsData = [];
      let paginationData = {};

      if (response?.data?.data?.products) {
        productsData = response.data.data.products;
        paginationData = response.data.data.pagination;
      } else if (response?.data?.products) {
        productsData = response.data.products;
        paginationData = response.data.pagination;
      } else if (response?.products) {
        productsData = response.products;
        paginationData = response.pagination;
      } else if (Array.isArray(response?.data?.data)) {
        productsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        productsData = response.data;
      } else if (Array.isArray(response)) {
        productsData = response;
      }

      console.log(
        `✅ Fetched ${productsData.length} products (Page ${currentPage} of ${paginationData?.pages || 1})`,
      );
      console.log("📊 Product categories in this page:", [
        ...new Set(productsData.map((p) => p.category)),
      ]);

      setProducts(productsData || []);
      setTotalProducts(paginationData?.total || productsData.length);
      setTotalPages(paginationData?.pages || paginationData?.totalPages || 1);

      // If current page is greater than total pages, reset to last page
      if (currentPage > (paginationData?.pages || 1)) {
        setCurrentPage(paginationData?.pages || 1);
      }
    } catch (err) {
      console.error("Fetch products error:", err);
      toastError(err.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await productService.getProductStats();

      let statsData = null;
      if (response?.data?.data) statsData = response.data.data;
      else if (response?.data) statsData = response.data;
      else if (response?.stats) statsData = response.stats;

      if (statsData) {
        setStats({
          total: statsData.total || 0,
          active: statsData.active || 0,
          inactive: statsData.inactive || 0,
          featured: statsData.featured || 0,
          new: statsData.new || 0,
          totalViews: statsData.totalViews || 0,
          totalImages: statsData.totalImages || 0,
        });
      } else {
        setStats({
          total: 0,
          active: 0,
          inactive: 0,
          featured: 0,
          new: 0,
          totalViews: 0,
          totalImages: 0,
        });
      }
    } catch (err) {
      console.error("Fetch stats error:", err);
      setStats({
        total: 0,
        active: 0,
        inactive: 0,
        featured: 0,
        new: 0,
        totalViews: 0,
        totalImages: 0,
      });
    }
  };

  const handleToggleStatus = async (id, isActive) => {
    try {
      await productService.toggleActive(id, isActive);
      toastSuccess(
        `Product ${isActive ? "activated" : "deactivated"} successfully`,
      );
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to update status");
    }
  };

  const handleToggleFeatured = async (id, isFeatured) => {
    try {
      await productService.toggleFeatured(id, isFeatured);
      toastSuccess(isFeatured ? "Product featured" : "Product unfeatured");
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to update featured status");
    }
  };

  const handleToggleAvailability = async (id, isAvailable) => {
    try {
      await productService.toggleAvailability(id, isAvailable);
      toastSuccess(isAvailable ? "Product available" : "Product unavailable");
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to update availability");
    }
  };

  const handleDelete = async (id) => {
    try {
      await productService.deleteProduct(id);
      toastSuccess("Product deleted");
      await fetchProducts();
      await fetchStats();
      setSelectedProducts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      toastError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedProducts.size) return;
    if (!window.confirm(`Delete ${selectedProducts.size} product(s)?`)) return;
    try {
      await productService.bulkDeleteProducts(Array.from(selectedProducts));
      toastSuccess(`${selectedProducts.size} product(s) deleted`);
      setSelectedProducts(new Set());
      setSelectAll(false);
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to delete products");
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedProducts.size) return;
    try {
      await productService.bulkUpdateStatus(
        Array.from(selectedProducts),
        isActive,
      );
      toastSuccess(
        `${selectedProducts.size} product(s) ${isActive ? "activated" : "deactivated"}`,
      );
      setSelectedProducts(new Set());
      setSelectAll(false);
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to update products");
    }
  };

  const handleBulkFeatured = async (isFeatured) => {
    if (!selectedProducts.size) return;
    try {
      await productService.bulkUpdateFeatured(
        Array.from(selectedProducts),
        isFeatured,
      );
      toastSuccess(
        `${selectedProducts.size} product(s) ${isFeatured ? "featured" : "unfeatured"}`,
      );
      setSelectedProducts(new Set());
      setSelectAll(false);
      await fetchProducts();
      await fetchStats();
    } catch (err) {
      toastError("Failed to update products");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedProducts(
      selectAll ? new Set() : new Set(products.map((p) => p.id)),
    );
  };

  const handleSelectProduct = (id) => {
    const newSet = new Set(selectedProducts);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedProducts(newSet);
    setSelectAll(newSet.size === products.length);
  };

  const statCards = [
    { icon: Package, label: "Total Products", value: stats?.total, trend: 12 },
    { icon: CheckCircle, label: "Active", value: stats?.active },
    { icon: XCircle, label: "Inactive", value: stats?.inactive },
    { icon: Star, label: "Featured", value: stats?.featured },
    { icon: Zap, label: "New", value: stats?.new },
  ];

  if (loading && !products.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Products
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage your product catalog and inventory
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
          >
            <Plus size={18} /> Add New Product
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard
              key={i}
              icon={card.icon}
              label={card.label}
              value={card.value}
              trend={card.trend}
            />
          ))}
        </div>

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by name, SKU, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Categories</option>
              <option value="food">Food</option>
              <option value="non-food">Non-Food</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <select
              value={featuredFilter}
              onChange={(e) => setFeaturedFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Products</option>
              <option value="featured">Featured Only</option>
              <option value="not-featured">Not Featured</option>
            </select>
            <button
              onClick={fetchProducts}
              className="px-4 py-2.5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border rounded-xl flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />{" "}
              Refresh
            </button>
          </div>
        </div>

        {selectedProducts.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedProducts.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkStatus(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkStatus(false)}
                className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                Deactivate
              </button>
              <button
                onClick={() => handleBulkFeatured(true)}
                className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Mark Featured
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <AnimatePresence>
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isSelected={selectedProducts.has(product.id)}
                onSelect={handleSelectProduct}
                onViewDetails={(p) => {
                  setSelectedProduct(p);
                  setShowDetailsModal(true);
                }}
                onEdit={(p) => {
                  setSelectedProduct(p);
                  setShowEditModal(true);
                }}
                onToggleStatus={handleToggleStatus}
                onToggleFeatured={handleToggleFeatured}
                onToggleAvailability={handleToggleAvailability}
                onDelete={(p) => {
                  setSelectedProduct(p);
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </AnimatePresence>
        </div>

        {!products.length && !loading && (
          <div className="text-center py-12 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border">
            <Package size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-gray-500">
              {searchTerm
                ? "Try adjusting your search"
                : "Click 'Add New Product' to create one"}
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-1.5">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailsModal && selectedProduct && (
          <DetailsModal
            product={selectedProduct}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedProduct(null);
            }}
            onEdit={(p) => {
              setShowDetailsModal(false);
              setSelectedProduct(p);
              setShowEditModal(true);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteModal && selectedProduct && (
          <DeleteModal
            product={selectedProduct}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedProduct(null);
            }}
            onConfirm={() => handleDelete(selectedProduct.id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateEditProductModal
            product={null}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchProducts();
              fetchStats();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && selectedProduct && (
          <CreateEditProductModal
            product={selectedProduct}
            onClose={() => {
              setShowEditModal(false);
              setSelectedProduct(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedProduct(null);
              fetchProducts();
              fetchStats();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsManagement;
